import { createContext, type ReactNode, useContext, useMemo } from 'react'

import { type PlaygroundRole } from './playgroundRole'

export type PlaygroundAccount = {
  email: string | null
  displayName: string | null
  role: PlaygroundRole
}

const PlaygroundRoleContext = createContext<PlaygroundAccount | null>(null)

export const PlaygroundRoleProvider = ({
  email,
  displayName,
  role,
  children,
}: PlaygroundAccount & { children: ReactNode }) => {
  const account = useMemo(
    () => ({ email, displayName, role }),
    [email, displayName, role]
  )

  return (
    <PlaygroundRoleContext.Provider value={account}>
      {children}
    </PlaygroundRoleContext.Provider>
  )
}

/**
 * Who is signed in. Throws outside the provider rather than returning null,
 * because the guard only renders children once a role exists — an absent one
 * means the component escaped the guard, and treating that as "no permissions"
 * would hide the mistake.
 */
export const usePlaygroundAccount = (): PlaygroundAccount => {
  const account = useContext(PlaygroundRoleContext)
  if (!account) {
    throw new Error('usePlaygroundAccount used outside FirebaseAuthGuard')
  }
  return account
}

export const usePlaygroundRole = (): PlaygroundRole =>
  usePlaygroundAccount().role

export const useIsSuperAdmin = (): boolean =>
  usePlaygroundRole().kind === 'superAdmin'

/** The partner a partner account sees, or null for anyone else. */
export const usePartnerId = (): string | null => {
  const role = usePlaygroundRole()
  return role.kind === 'partner' ? role.partnerId : null
}
